import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { Deal, DealType } from "src/business/deal";
import { CustomLoadingButton, RangeCalendar, SnackBar } from "src/design";
import {
  useAddRestaurantDeal,
  useUpdateRestaurantDeal,
} from "src/data/deals/queries/deals";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextSecondary,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";

const DEFAULT_START = () => dayjs().hour(12).minute(0).second(0);
const DEFAULT_END = () => dayjs().hour(13).minute(0).second(0);


const formatTimeSlot = (start: Dayjs, end: Dayjs): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(start.hour())}:${pad(start.minute())}-${pad(end.hour())}:${pad(end.minute())}`;
};

interface DateSelection {
  start: string;
  end: string;
}

const expandSelection = (selection: DateSelection): string[] => {
  const days: string[] = [];
  const last = dayjs(selection.end);
  for (let cursor = dayjs(selection.start); !cursor.isAfter(last); cursor = cursor.add(1, "day")) {
    days.push(cursor.format("YYYY-MM-DD"));
  }
  return days;
};

const formatSelectionLabel = (selection: DateSelection): string => {
  const start = dayjs(selection.start);
  const end = dayjs(selection.end);
  if (selection.start === selection.end) {
    return start.format("MMM D, YYYY");
  }
  if (start.year() === end.year() && start.month() === end.month()) {
    return `${start.format("MMM D")} – ${end.format("D, YYYY")}`;
  }
  if (start.year() === end.year()) {
    return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
};

interface Props {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  deal?: Deal;
  onSuccess: () => void;
}

export const CreateDealModal = ({
  open,
  onClose,
  restaurantId,
  deal,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const { mutate: addDeal, isPending: adding } = useAddRestaurantDeal();
  const { mutate: updateDeal, isPending: updating } = useUpdateRestaurantDeal();

  const existingLines = deal?.content.split("\n") ?? [];
  const [name, setName] = useState(existingLines[0] ?? "");
  const [description, setDescription] = useState(existingLines.slice(1).join("\n"));
  const [type, setType] = useState<DealType>(deal?.type ?? DealType.DEAL);
  const [dateSelections, setDateSelections] = useState<DateSelection[]>(
    deal
      ? [...new Set(deal.days)].sort().map((day) => ({ start: day, end: day }))
      : []
  );
  const [hours, setHours] = useState<string[]>(
    deal ? [...new Set(deal.timeRanges)] : []
  );
  const [active, setActive] = useState(deal?.active ?? true);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(DEFAULT_START());
  const [endTime, setEndTime] = useState<Dayjs | null>(DEFAULT_END());
  const [error, setError] = useState(false);
  const [successSnack, setSuccessSnack] = useState(false);
  const [errorSnack, setErrorSnack] = useState(false);

  const isEditing = !!deal;

  const handleRangeSelected = (start: Dayjs, end: Dayjs) => {
    const from = (start.isBefore(end) ? start : end).startOf("day");
    const to = (start.isBefore(end) ? end : start).startOf("day");
    const today = dayjs().startOf("day");
    const clampedFrom = from.isBefore(today) ? today : from;
    setCalendarAnchor(null);
    if (clampedFrom.isAfter(to)) return;

    const newSelection: DateSelection = {
      start: clampedFrom.format("YYYY-MM-DD"),
      end: to.format("YYYY-MM-DD"),
    };
    setDateSelections((prev) =>
      prev.some((s) => s.start === newSelection.start && s.end === newSelection.end)
        ? prev
        : [...prev, newSelection]
    );
  };

  const handleAddSlot = () => {
    if (!startTime || !endTime) return;
    const slot = formatTimeSlot(startTime, endTime);
    if (!hours.includes(slot)) {
      setHours((prev) => [...prev, slot]);
    }
    setStartTime(DEFAULT_START());
    setEndTime(DEFAULT_END());
  };

  const allDays = [...new Set(dateSelections.flatMap(expandSelection))].sort();
  const isValid = name.trim() && allDays.length > 0 && hours.length > 0;

  const handleSubmit = () => {
    if (!isValid) {
      setError(true);
      return;
    }
    const content = description.trim() ? `${name}\n${description}` : name;
    const payload = {
      content,
      active,
      type,
      days: allDays,
      timeRanges: hours
    };

    if (isEditing) {
      updateDeal(
        { restaurantId, dealId: deal!.id, payload },
        {
          onSuccess: () => {
            setSuccessSnack(true);
            onSuccess();
            onClose();
          },
          onError: () => setErrorSnack(true),
        }
      );
    } else {
      addDeal(
        { restaurantId, payload },
        {
          onSuccess: () => {
            onSuccess();
            onClose();
          },
          onError: () => setErrorSnack(true),
        }
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: radiusCard,
            boxShadow: shadowCard,
            border: `1px solid ${colorBorder}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "18px", lineHeight: 1.3 }}>
                {isEditing ? t("deals.modal.editTitle") : t("deals.modal.createTitle")}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: colorTextMuted, mt: 0.25 }}>
                {t("deals.modal.subtitle")}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider sx={{ mx: 3, mt: 1.5 }} />

        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label={t("deals.modal.name")}
              required
              fullWidth
              size="small"
              placeholder="e.g., Summer Special, Live Jazz Night"
              value={name}
              error={error && !name.trim()}
              onChange={(e) => setName(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />

            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>{t("deals.modal.type")}</InputLabel>
                <Select
                  value={type}
                  label={t("deals.modal.type")}
                  onChange={(e) => setType(e.target.value as DealType)}
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem value={DealType.DEAL}>{t("deals.deal")}</MenuItem>
                  <MenuItem value={DealType.EVENT}>{t("deals.event")}</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: "12px", color: colorTextMuted, mt: 0.75, px: 0.5 }}>
                {t("deals.modal.typeHint")}
              </Typography>
            </Box>

            <TextField
              label={t("deals.modal.description")}
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder={t("deals.modal.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />

            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1 }}>
                {t("deals.modal.dates")} *
              </Typography>
              <TextField
                value=""
                placeholder={t("deals.modal.selectDates")}
                size="small"
                fullWidth
                error={error && dateSelections.length === 0}
                onClick={(e) => setCalendarAnchor(e.currentTarget)}
                InputProps={{
                  readOnly: true,
                  endAdornment: <CalendarMonthIcon fontSize="small" sx={{ color: colorTextMuted }} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", cursor: "pointer" },
                  "& input": { cursor: "pointer" },
                }}
              />
              <Popover
                open={Boolean(calendarAnchor)}
                anchorEl={calendarAnchor}
                onClose={() => setCalendarAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <RangeCalendar onRangeSelected={handleRangeSelected} />
              </Popover>

              {dateSelections.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: "12px", color: colorTextMuted, mb: 0.75 }}>
                    {t("deals.modal.selectedDates")} ({allDays.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {dateSelections.map((selection) => (
                      <Chip
                        key={`${selection.start}_${selection.end}`}
                        label={formatSelectionLabel(selection)}
                        size="small"
                        onDelete={() =>
                          setDateSelections((prev) =>
                            prev.filter(
                              (s) => !(s.start === selection.start && s.end === selection.end)
                            )
                          )
                        }
                        sx={{
                          fontSize: "12px",
                          backgroundColor: colorSurface,
                          border: `1px solid ${colorBorder}`,
                          "& .MuiChip-deleteIcon": { fontSize: 14, color: colorTextMuted },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1 }}>
                {t("deals.modal.timeSlots")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TimePicker
                  label={t("deals.addNewDeal.startTime")}
                  value={startTime}
                  ampm
                  minutesStep={30}
                  onChange={setStartTime}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    />
                  )}
                />
                <TimePicker
                  label={t("deals.addNewDeal.endTime")}
                  value={endTime}
                  ampm
                  minutesStep={30}
                  minTime={startTime ?? undefined}
                  onChange={setEndTime}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    />
                  )}
                />
                <Button
                  onClick={handleAddSlot}
                  disabled={!startTime || !endTime}
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                  sx={{
                    height: 40,
                    px: 2,
                    fontSize: "13px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#111827",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#374151" },
                    "&.Mui-disabled": { backgroundColor: colorBorder, color: colorTextMuted },
                  }}
                >
                  {t("deals.modal.addSlot")}
                </Button>
              </Box>

              {hours.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: "12px", color: colorTextMuted, mb: 0.75 }}>
                    {t("deals.modal.timeSlots")} ({hours.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {hours.map((slot) => (
                      <Chip
                        key={slot}
                        label={slot}
                        size="small"
                        onDelete={() => setHours((prev) => prev.filter((h) => h !== slot))}
                        sx={{
                          fontSize: "12px",
                          backgroundColor: colorSurface,
                          border: `1px solid ${colorBorder}`,
                          "& .MuiChip-deleteIcon": { fontSize: 14, color: colorTextMuted },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#10B981" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#10B981",
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {t("deals.modal.enableDeal")}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: colorTextMuted }}>
                    {t("deals.modal.enableDealHint")}
                  </Typography>
                </Box>
              }
              sx={{ mx: 0, alignItems: "flex-start", gap: 0.5 }}
            />

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Box
                component="button"
                onClick={onClose}
                sx={{
                  px: 2.5,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: 500,
                  border: `1px solid ${colorBorder}`,
                  borderRadius: "8px",
                  backgroundColor: colorSurface,
                  cursor: "pointer",
                  color: colorTextSecondary,
                  "&:hover": { backgroundColor: "#F9FAFB" },
                }}
              >
                {t("deals.cancel")}
              </Box>
              <CustomLoadingButton
                loading={adding || updating}
                onSubmit={handleSubmit}
                label={isEditing ? t("deals.save") : t("deals.modal.createButton")}
                variant="contained"
                color="success"
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <SnackBar
        open={successSnack}
        onClose={() => setSuccessSnack(false)}
        message={t("deals.updateSuccess")}
        severity="success"
      />
      <SnackBar
        open={errorSnack}
        onClose={() => setErrorSnack(false)}
        message={t("deals.updateError")}
        severity="error"
      />
    </LocalizationProvider>
  );
};
